<template>
  <div class="relative" ref="selectRef">
    <!-- Input de búsqueda / Display -->
    <div
      @click="toggleDropdown"
      class="w-full px-2 py-1 text-sm rounded border bg-white dark:bg-gray-800 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-200 cursor-pointer flex items-center justify-between"
      :class="{ 'ring-1 ring-violet-200': isOpen }"
    >
      <span v-if="selectedOption" class="truncate">{{ selectedOption[labelKey] }}</span>
      <span v-else class="text-gray-400 dark:text-gray-500">{{ placeholder }}</span>
      <svg
        class="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
      </svg>
    </div>

    <!-- Dropdown con búsqueda -->
    <div
      v-if="isOpen"
      class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-hidden"
    >
      <!-- Input de búsqueda -->
      <div class="p-2 border-b border-gray-200 dark:border-gray-700">
        <div class="relative">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="w-full px-3 py-2 pl-8 text-sm rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
            :placeholder="searchPlaceholder"
            @keydown.down.prevent="navigateDown"
            @keydown.up.prevent="navigateUp"
            @keydown.enter.prevent="selectHighlighted"
            @keydown.esc="closeDropdown"
          />
          <svg
            class="absolute left-2 top-2.5 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
      </div>

      <!-- Lista de opciones filtradas -->
      <div class="overflow-y-auto max-h-64">
        <div
          v-if="filteredOptions.length === 0"
          class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center"
        >
          No se encontraron resultados
        </div>
        <div
          v-for="(option, index) in filteredOptions"
          :key="option[valueKey]"
          @click="selectOption(option)"
          @mouseenter="highlightedIndex = index"
          class="px-4 py-2 text-sm cursor-pointer transition"
          :class="{
            'bg-violet-100 dark:bg-violet-900/30': highlightedIndex === index,
            'bg-violet-50 dark:bg-violet-900/20 font-medium': isSelected(option),
            'hover:bg-gray-100 dark:hover:bg-gray-700': highlightedIndex !== index && !isSelected(option)
          }"
        >
          <div class="flex items-center justify-between">
            <span class="truncate">{{ option[labelKey] }}</span>
            <svg
              v-if="isSelected(option)"
              class="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0 ml-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null
  },
  options: {
    type: Array,
    required: true,
    default: () => []
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  valueKey: {
    type: String,
    default: 'value'
  },
  placeholder: {
    type: String,
    default: 'Seleccionar opción...'
  },
  searchPlaceholder: {
    type: String,
    default: 'Buscar...'
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const selectRef = ref(null)
const searchInput = ref(null)
const isOpen = ref(false)
const searchQuery = ref('')
const highlightedIndex = ref(0)

// Opción seleccionada
const selectedOption = computed(() => {
  return props.options.find(opt => opt[props.valueKey] === props.modelValue) || null
})

// Opciones filtradas por búsqueda
const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options
  }

  const query = searchQuery.value.toLowerCase()
  return props.options.filter(option => {
    const label = String(option[props.labelKey]).toLowerCase()
    return label.includes(query)
  })
})

// Verificar si una opción está seleccionada
const isSelected = (option) => {
  return option[props.valueKey] === props.modelValue
}

// Toggle dropdown
const toggleDropdown = async () => {
  isOpen.value = !isOpen.value

  if (isOpen.value) {
    await nextTick()
    searchInput.value?.focus()

    // Si hay una opción seleccionada, encontrar su índice en la lista filtrada
    if (selectedOption.value) {
      const index = filteredOptions.value.findIndex(opt => opt[props.valueKey] === props.modelValue)
      if (index !== -1) {
        highlightedIndex.value = index
      }
    }
  } else {
    searchQuery.value = ''
    highlightedIndex.value = 0
  }
}

// Cerrar dropdown
const closeDropdown = () => {
  isOpen.value = false
  searchQuery.value = ''
  highlightedIndex.value = 0
}

// Seleccionar opción
const selectOption = (option) => {
  emit('update:modelValue', option[props.valueKey])
  emit('change', option[props.valueKey])
  closeDropdown()
}

// Navegación con teclado
const navigateDown = () => {
  if (highlightedIndex.value < filteredOptions.value.length - 1) {
    highlightedIndex.value++
  }
}

const navigateUp = () => {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--
  }
}

const selectHighlighted = () => {
  if (filteredOptions.value[highlightedIndex.value]) {
    selectOption(filteredOptions.value[highlightedIndex.value])
  }
}

// Cerrar al hacer click fuera
const handleClickOutside = (event) => {
  if (selectRef.value && !selectRef.value.contains(event.target)) {
    closeDropdown()
  }
}

// Reset highlighted index cuando cambia la búsqueda
watch(searchQuery, () => {
  highlightedIndex.value = 0
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
